import {Component, OnChanges, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { Location, NgIf } from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {HeaderService} from '../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {BaseComponent} from '../../core/base.component';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {NotificationService} from '../../notification/notification.service';
import {AuthService} from '../../core/security/auth.service';
import {ProMessagingAutomationService} from '../pro-messaging-automation.service';
import {DateUtils} from '../../core/utilities/date-time-helpers';
import {RRule} from 'rrule';
import 'moment-timezone';
import {DateTime} from 'luxon';
import {RolesEnum} from '../../core/security/roles.enum';
import {AutomationModel, AutomationPhoneNumberModel, CurrentUser, LookupModel} from '../../api/models';
import {MatDialog} from '@angular/material/dialog';
import {MatConfirmDialogComponent} from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import {PhoneNumberPipe} from '../../core/pipes/phone-number.pipe';
import { MatCard, MatCardTitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatMultiselectComponent } from '../../editors/mat-multiselect-input/mat-multiselect.component';
import { MatRecipientsMultiselectComponent } from '../../editors/mat-recipients-multiselect/mat-recipients-multiselect.component';
import { MatTextareaInputComponent } from '../../editors/mat-textarea-input/mat-textarea-input.component';
import { MatRruleFormGroupComponent } from '../../editors/mat-rrule-form-group/mat-rrule-form-group.component';
import { FlexModule } from '@angular/flex-layout';
import { MatButton } from '@angular/material/button';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-pro-messaging-automation-edit',
    templateUrl: './edit-schedule.component.html',
    styleUrls: ['./edit-schedule.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatCard,
        MatCardTitle,
        MatCardContent,
        NgIf,
        MatTextInputComponent,
        MatCheckboxInputComponent,
        MatMultiselectComponent,
        MatRecipientsMultiselectComponent,
        MatTextareaInputComponent,
        MatRruleFormGroupComponent,
        MatCardActions,
        FlexModule,
        MatButton,
        Angular2PromiseButtonModule,
        MatTooltip,
        MatIcon,
    ],
})
export class ProMessagingAutomationEditScheduleComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('zonesControl') zonesControl;

  form: UntypedFormGroup;
  submitted: boolean;
  serverError: boolean;
  model: AutomationModel = {
    name: '',
    id: 'new',
    isEnabled: true,
    keyword: [],
    replyMessage: '',
    schedule: [],
    recipients: [],
    phoneNumbers: []
  };

  userTimezone = 'America/New_York';
  recurringForm: UntypedFormGroup;
  recurringNextDate: Date;
  rRule: RRule;

  buttonSubmitPromise: Promise<void>;
  availablePhoneNumbers: LookupModel[];
  phoneNumberDisplayPipe = new PhoneNumberPipe()

  private paramsSubscription;

  constructor(route: ActivatedRoute, titleService: Title, headerService: HeaderService, authService: AuthService,
              public dialog: MatDialog,
              public automationService: ProMessagingAutomationService,
              private fb: UntypedFormBuilder,
              private location: Location, private notificationService: NotificationService) {
    super(route, headerService, titleService, authService);

    this.editRoles = [RolesEnum.MessagingAutomation_Edit, RolesEnum.MessagingAutomation_Delete];
    this.deleteRoles = [RolesEnum.MessagingAutomation_Delete];

    this.paramsSubscription = this.route.params.subscribe(async (params) => {
      this.model.id = params['id'];
      console.log(`model.id - ${this.model.id}`);

      this.availablePhoneNumbers = await this.automationService.lookupPhoneNumbers();

      if (this.model.id !== 'new') {
        this.model = await this.automationService.getAutomationItem(this.model.id);
        this.ngOnChanges();
      }
    });

    this.headerService.showBackButton(true);

    this.createForm();
  }

  onCurrentUserChanged(user: CurrentUser) {
    super.onCurrentUserChanged(user);

    this.userTimezone = user.settings.timezone || 'America/New_York';
  }

  onRRuleChanged($event: [RRule, Date]) {
    [this.rRule, this.recurringNextDate] = $event;
  }

  async ngOnInit() {
    super.ngOnInit();
    this.headerService.showBackButton(true);

    this.ngOnChanges();
  }

  ngOnChanges() {
    const recurring = this.preFillRecurring(this.model.rRule);
    const phoneNumbersIds: string[] = (this.model.phoneNumbers || []).map(x => x.id);
    const phoneNumbers: string[] = (this.availablePhoneNumbers || [])
      .filter(x => phoneNumbersIds.indexOf(`${x.id}`) > -1)
      .map(x => `${x.id}`);

    this.form.reset({
      name: this.model.name,
      keyword: this.model.keyword,
      isEnabled: this.model.isEnabled,
      replyMessage: this.model.replyMessage,
      recipients: this.model.recipients,
      recurring: recurring,
      phoneNumbers: phoneNumbers,
    });
  }

  preFillRecurring(ruleText: string) {
    console.log('ruleText: ', ruleText);

    if (!ruleText) {
      return {
        freq: RRule.DAILY,
        interval: 1,
        dtstart: DateTime.local().setZone(this.userTimezone).toJSDate(),
        endType: -1,
        monthlyType: 'bymonthday',
        yearlyType: 'bymonthday',
      };
    } else {
      this.rRule = RRule.fromString(ruleText);
      const ruleOptions = this.rRule.options;

      const date = DateTime.fromJSDate(ruleOptions.dtstart, {zone: this.userTimezone});
      const dtstart = DateUtils.localDateFromDateTime(date) || DateUtils.localDateFromDateTime(DateTime.local());

      return {
        freq: ruleOptions.freq,
        interval: ruleOptions.interval || 1,
        dtstart: dtstart,
        endType: ruleOptions.until ? 2 : ruleOptions.count ? 1 : -1,
        until: ruleOptions.until || undefined,
        count: ruleOptions.count || 1,
        byDay: ruleOptions.byweekday || undefined,
        byMonthDay: !!ruleOptions.bymonthday && ruleOptions.bymonthday.length > 0 ? ruleOptions.bymonthday[0] : undefined,
        bySetPos: !!ruleOptions.bysetpos && ruleOptions.bysetpos.length > 0 ? ruleOptions.bysetpos[0] : undefined,
        monthlyType: !!ruleOptions.bymonthday && ruleOptions.bymonthday.length > 0 ? 'bymonthday' : 'bysetpos',
        yearlyType: !!ruleOptions.bymonthday && ruleOptions.bymonthday.length > 0 ? 'bymonthday' : 'bysetpos',
        byMonth: !!ruleOptions.bymonth && ruleOptions.bymonth.length > 0 ? ruleOptions.bymonth[0] : undefined,
      };
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }

  async onSubmit() {
    this.serverError = false;

    if (this.form.valid) {
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit(): Promise<void> {

    try {
      this.submitted = true;
      const reqData = this.prepareFormModel();
      await this.automationService.saveAutomation(reqData);
      this.notificationService.notifySuccess('Record updated successfully');
      this.goBack();
    } catch (e) {
      this.serverError = false;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): AutomationModel {
    const formModel = this.form.value;

    const phoneNumbersIds: string[] = formModel.phoneNumbers || [];
    const phoneNumbers: AutomationPhoneNumberModel[] = (this.availablePhoneNumbers || [])
      .filter(x => phoneNumbersIds.indexOf(`${x.id}`) > -1)
      .map(x => {
        return {
          id: `${x.id}`,
          phoneNumber: x.text
        };
      })

    return {
      id: this.model.id,
      name: formModel.name,
      keyword: formModel.keyword,
      isEnabled: formModel.isEnabled,
      replyMessage: formModel.replyMessage,
      schedule: [],
      recipients: formModel.recipients,
      rRule: this.rRule ? this.rRule.toString() : undefined,
      nextRecurringDate: this.recurringNextDate
        ? this.recurringNextDate.toISOString()
        : undefined,
      phoneNumbers: phoneNumbers
    };
  }

  goBack(): void {
    this.location.back();
  }

  createForm(): void {
    this.recurringForm = this.fb.group({
      'freq': [RRule.DAILY],
      'interval': [1],
      'byDay': [],
      'byMonthDay': [],
      'byMonth': [],
      'monthlyType': ['bymonthday'],
      'yearlyType': [],
      'bySetPos': [1],
      'endType': [-1],
      'count': [1],
      'until': [],
      'dtstart': [],
    });

    this.form = this.fb.group({
        'name': ['', Validators.required],
        'isEnabled': [true],
        'keyword': [''],
        'replyMessage': ['', Validators.required],
        'recipients': [[], Validators.required],
        'recurring': this.recurringForm,
        'phoneNumbers': [[]],
      }
    );
  }

  async onDeleteClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Record?',
        text: 'Are you sure you want to delete this record?',
        actionTitle: 'Delete',
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.automationService.deleteAutomationItem(this.model.id);
        this.notificationService.notifySuccess('Record has been deleted successfully');
        this.headerService.goBack();
      }
    });
  }
}

